"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ArrowUpRight, Eye, ThumbsDown, ThumbsUp } from "lucide-react"
import { GrassTuft } from "@/components/icons/grass-tuft"
import { VoteButtons, type VoteUpdate } from "./vote-buttons"
import { ShareButtons } from "./share-buttons"
import { ReportPhotoDialog } from "./report-photo-dialog"
import { formatDistanceToNow } from "date-fns"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"
import { communityAnalytics } from "@/lib/analytics"

interface LawnPhoto {
  id: string
  slug: string
  image_url: string
  thumbnail_url?: string
  title: string
  score: number
  upvotes: number
  downvotes: number
  grass_type?: string
  zip_code?: string
  created_at: string
  view_count?: number
  share_count?: number
  social_handle?: string
}

interface PhotoCardProps {
  photo: LawnPhoto
  eager?: boolean
}

export function PhotoCard({ photo, eager = false }: PhotoCardProps) {
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const imageTriggerRef = useRef<HTMLButtonElement>(null)
  const [voteTotals, setVoteTotals] = useState({
    score: photo.score,
    upvotes: photo.upvotes,
    downvotes: photo.downvotes,
  })

  useEffect(() => {
    setVoteTotals({
      score: photo.score,
      upvotes: photo.upvotes,
      downvotes: photo.downvotes,
    })
  }, [photo.id, photo.score, photo.upvotes, photo.downvotes])

  const imageUrl = photo.thumbnail_url || photo.image_url
  const timeAgo = formatDistanceToNow(new Date(photo.created_at), { addSuffix: true })
  const socialHandle = photo.social_handle?.trim()
  const displayHandle = socialHandle ? (socialHandle.startsWith('@') ? socialHandle : `@${socialHandle}`) : null

  // Check if photo is new (less than 7 days old)
  const isNewPhoto = () => {
    const createdDate = new Date(photo.created_at)
    const now = new Date()
    const daysDiff = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
    return daysDiff < 7
  }

  return (
    <div
      className="ds-glass group overflow-hidden rounded-3xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg motion-reduce:transition-none"
    >
      {/* The photo opens a focused viewer; the title below opens the durable detail URL. */}
      <button
        ref={imageTriggerRef}
        type="button"
        className="relative block aspect-4/3 w-full cursor-zoom-in overflow-hidden border-0 p-0 text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-500 focus-visible:ring-inset"
        onClick={() => {
          communityAnalytics.photoOpened(photo.id, 'gallery_quick_view')
          setImageModalOpen(true)
        }}
        aria-label={`View full-size photo: ${photo.title}`}
      >
        <Image
          src={imageUrl}
          alt={photo.title}
          fill
          loading={eager ? "eager" : "lazy"}
          className="object-cover transition-transform duration-300 group-hover:scale-105"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />

        {/* Grass type badge */}
        {photo.grass_type && (
          <div className="absolute left-3 top-3">
            <Badge variant="outline" className="rounded-full border-white/70 bg-white/90 px-2.5 py-1 text-xs font-semibold text-green-950 shadow-sm backdrop-blur-sm">
              {photo.grass_type}
            </Badge>
          </div>
        )}

        {/* NEW badge - smaller on mobile */}
        {isNewPhoto() && (
          <div className={`absolute left-3 ${photo.grass_type ? 'top-12' : 'top-3'}`}>
            <Badge
              className="rounded-full border-0 bg-amber-300 px-2 py-1 text-[10px] font-black tracking-wide text-amber-950 shadow-sm"
            >
              <GrassTuft className="mr-1 h-3 w-3" />
              NEW LAWN
            </Badge>
          </div>
        )}
      </button>

      <div className="p-4 md:p-5">
        <h3 className="font-editorial text-xl font-semibold leading-snug text-green-950 md:text-2xl">
          <Link
            href={`/community/${photo.slug || photo.id}`}
            onClick={() => communityAnalytics.photoOpened(photo.id, 'gallery_card')}
            className="group/title flex items-start justify-between gap-3 rounded-sm transition-colors hover:text-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2"
          >
            <span className="line-clamp-2">{photo.title}</span>
            <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-green-700 transition-transform group-hover/title:-translate-y-0.5 group-hover/title:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
          </Link>
        </h3>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-green-900/65">
          {displayHandle && <span className="font-semibold text-green-700">{displayHandle}</span>}
          <span>{timeAgo}</span>
          <span className="inline-flex items-center gap-1" aria-label={`${photo.view_count || 0} views`}>
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            {photo.view_count || 0}
          </span>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-green-950/10 pt-3">
          <VoteButtons
            photoId={photo.id}
            currentScore={voteTotals.score}
            compact
            onVoteUpdated={(update: VoteUpdate) => {
              setVoteTotals({
                score: update.score,
                upvotes: update.upvotes,
                downvotes: update.downvotes,
              })
            }}
          />
          <div className="flex items-center gap-0.5">
            <ShareButtons
              photoSlug={photo.slug}
              photoId={photo.id}
              title={photo.title}
              compact
            />
            <ReportPhotoDialog photoId={photo.id} photoTitle={photo.title} compact />
          </div>
        </div>
      </div>

      {/* Image Modal - Adaptive for portrait/landscape */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent
          className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-3xl max-h-[90vh] p-0 overflow-hidden gap-0 w-auto"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            imageTriggerRef.current?.focus()
          }}
        >
          {/* Image container - adapts to image dimensions */}
          <div className="relative bg-gray-100 flex items-center justify-center">
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              centerOnInit={true}
            >
              {() => (
                <TransformComponent
                  wrapperClass="w-full! flex! items-center! justify-center!"
                  contentClass="flex! items-center! justify-center!"
                >
                  <img
                    src={photo.image_url}
                    alt={photo.title}
                    className="max-h-[60vh] max-w-full w-auto h-auto object-contain"
                  />
                </TransformComponent>
              )}
            </TransformWrapper>
          </div>

          {/* Content */}
          <div className="px-5 py-4 space-y-4">
            <DialogHeader className="p-0 space-y-1">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                {photo.title}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Community lawn photo submitted {timeAgo} with a score of {voteTotals.score}.
              </DialogDescription>
            </DialogHeader>

            {/* Info row - stats left, date right */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4 text-gray-500">
                <div className="flex items-center gap-1.5 text-green-600 font-medium" aria-label={`${voteTotals.upvotes} upvotes`}>
                  <ThumbsUp className="w-4 h-4" aria-hidden="true" />
                  <span>{voteTotals.upvotes}</span>
                </div>
                <div className="flex items-center gap-1.5" aria-label={`${voteTotals.downvotes} downvotes`}>
                  <ThumbsDown className="w-4 h-4" aria-hidden="true" />
                  <span>{voteTotals.downvotes}</span>
                </div>
                <div className="flex items-center gap-1.5" aria-label={`${photo.view_count || 0} views`}>
                  <Eye className="w-4 h-4" aria-hidden="true" />
                  <span>{photo.view_count || 0}</span>
                </div>
                {photo.grass_type && (
                  <span className="text-green-600 font-medium">{photo.grass_type}</span>
                )}
              </div>
              <span className="text-gray-400">{timeAgo}</span>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <Link
                href={`/community/${photo.slug || photo.id}`}
                className="inline-flex min-h-11 items-center text-sm font-semibold text-green-800 hover:text-green-600"
              >
                Open details
              </Link>
              <div className="flex items-center gap-0.5">
                <ShareButtons
                  photoSlug={photo.slug}
                  photoId={photo.id}
                  title={photo.title}
                  compact
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
